import React from 'react';
import { RSVPStats } from '../../../types/invitation';
import { EXPORTABLE_FIELDS } from '../types';

interface MainExportModalProps {
  rsvpStats: RSVPStats;
  exportingData: boolean;
  selectedExportFields: Set<string>;
  onSetQuickPreset: (preset: 'basic' | 'full') => void;
  onToggleField: (fieldKey: string) => void;
  onResetFields: () => void;
  onExport: () => void;
  onClose: () => void;
}

export const MainExportModal: React.FC<MainExportModalProps> = ({
  rsvpStats,
  exportingData,
  selectedExportFields,
  onSetQuickPreset,
  onToggleField,
  onResetFields,
  onExport,
  onClose,
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-wedding-text-title mb-4">匯出資料選項</h3>

          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-wedding-text-title mb-3">快速選項</h4>
              <div className="flex space-x-2">
                <button
                  onClick={() => onSetQuickPreset('basic')}
                  className="px-3 py-2 text-sm bg-wedding-bg border border-wedding-pink/30 text-wedding-text-title rounded-md hover:bg-wedding-pink/5"
                >
                  基本資訊
                </button>
                <button
                  onClick={() => onSetQuickPreset('full')}
                  className="px-3 py-2 text-sm bg-wedding-bg border border-wedding-pink/30 text-wedding-text-title rounded-md hover:bg-wedding-pink/5"
                >
                  完整資訊
                </button>
                <button
                  onClick={onResetFields}
                  className="px-3 py-2 text-sm bg-wedding-bg border border-wedding-pink/30 text-wedding-text-title rounded-md hover:bg-wedding-pink/5"
                >
                  重設
                </button>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-wedding-text-title mb-3">自訂欄位</h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {EXPORTABLE_FIELDS.map((field) => (
                  <label key={field.key} className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedExportFields.has(field.key)}
                      onChange={() => onToggleField(field.key)}
                      disabled={field.required}
                      className="rounded border-gray-300 text-wedding-pink focus:ring-wedding-pink disabled:opacity-50"
                    />
                    <span className={`text-sm ${field.required ? 'text-wedding-text-title font-medium' : 'text-wedding-text-body'}`}>
                      {field.label}
                      {field.required && <span className="text-wedding-pink ml-1">(必要)</span>}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="bg-wedding-bg/30 rounded-lg p-3">
              <div className="text-sm text-wedding-text-body">
                將匯出 <span className="font-semibold text-wedding-pink">{selectedExportFields.size}</span> 個欄位，
                共 <span className="font-semibold text-wedding-pink">{rsvpStats.total}</span> 筆資料
              </div>
              <div className="text-xs text-wedding-text-body mt-1">
                已選欄位：{Array.from(selectedExportFields).map(key =>
                  EXPORTABLE_FIELDS.find(f => f.key === key)?.label
                ).join('、')}
              </div>
            </div>
          </div>

          <div className="flex space-x-3 mt-6">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              取消
            </button>
            <button
              onClick={onExport}
              disabled={exportingData || selectedExportFields.size === 0}
              className="flex-1 px-4 py-2 bg-wedding-pink text-white rounded-md hover:bg-wedding-pink/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportingData ? '匯出中...' : `匯出 (${selectedExportFields.size} 欄位)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
